import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, ShieldCheck, ArrowLeft } from "lucide-react";
import axios from "axios";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PageBackdrop } from "@/components/PageBackdrop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
};

const PayPage = () => {
  usePageMeta("Vagus Digital | Pay your deposit");
  const [reference, setReference] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [mode, setMode] = useState(null);

  useEffect(() => {
    axios.get(`${API}/payments/mode`).then(({ data }) => setMode(data.mode)).catch(() => {});
  }, []);

  const pay = async (e) => {
    e.preventDefault();
    if (status === "redirecting") return;
    setStatus("redirecting");
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/payments/checkout`,
        {
          lookup_key: "project_deposit",
          reference: reference.trim().slice(0, 100),
          origin_url: window.location.origin,
        },
        { timeout: 30000 }
      );
      window.location.href = data.checkout_url;
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Couldn't start checkout. Please try again in a moment."
      );
    }
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-paper pb-24 pt-32 lg:pt-40">
      <PageBackdrop src="/art/hero.webp" position="object-center" />
      <div className="relative mx-auto max-w-2xl px-6 lg:px-10">
        <Link
          to="/"
          data-testid="pay-home-link"
          className="inline-flex items-center gap-2 text-sm font-medium text-jade transition-opacity duration-300 hover:opacity-70"
        >
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>

        <motion.div
          {...fadeUp}
          transition={{ duration: 0.7 }}
          className="mt-10 rounded-2xl bg-white p-10 shadow-ambient lg:p-12"
          data-testid="pay-card"
        >
          <p className="text-xs font-medium uppercase tracking-[0.3em] text-jade">
            Payments
          </p>
          <h1 className="mt-4 font-display text-3xl font-light tracking-tight text-ink sm:text-4xl">
            Pay your <em className="italic text-jade">project deposit</em>.
          </h1>
          <p className="mt-5 leading-relaxed text-ink2">
            Settling an agreed deposit or invoice? Pay securely by card.
            You'll get a receipt from Stripe the moment it clears, and I'll
            see it on my side too.
          </p>

          <div className="mt-8 flex items-baseline gap-2">
            <span className="font-display text-5xl font-light text-ink">£250.00</span>
            <span className="text-sm text-ink2/70">project deposit (GBP)</span>
          </div>

          <form onSubmit={pay} className="mt-8 space-y-5" data-testid="pay-form">
            <div>
              <label htmlFor="pay-reference" className="mb-2 block text-xs font-medium uppercase tracking-[0.15em] text-ink2">
                Invoice or project reference (optional)
              </label>
              <input
                id="pay-reference"
                data-testid="pay-reference-input"
                className="w-full rounded-xl border border-jade/20 bg-paper/60 px-4 py-3 text-sm text-ink placeholder:text-ink2/40 transition-colors duration-300"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="e.g. VD-2026-014 or your business name"
                maxLength={100}
              />
            </div>

            {error && (
              <p className="text-sm text-red-700" data-testid="pay-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="pay-checkout-button"
              disabled={status === "redirecting"}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-jade px-8 py-3.5 text-sm font-medium text-paper transition-colors duration-300 hover:bg-ink disabled:cursor-not-allowed disabled:opacity-60"
            >
              <CreditCard className="h-4 w-4" />
              {status === "redirecting" ? "Opening Stripe…" : "Continue to secure checkout"}
            </button>

            <p className="flex items-center justify-center gap-2 text-xs text-ink2/60">
              <ShieldCheck className="h-3.5 w-3.5" />
              Card details are handled by Stripe. They never touch this site.
            </p>
            {mode === "test" && (
              <p className="text-center text-xs text-ink2/40" data-testid="pay-test-note">
                Test mode: no real money is taken. Use card 4242 4242 4242 4242,
                any future expiry and any CVC.
              </p>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default PayPage;